import { PosthogFeatureFlags } from '@domgen/shared/enums/featureFlags';
import { calculateAgeInMonths } from '@domgen/shared/utils/date/calculateAgeInMonths';
import { usePostHog, useFeatureFlagEnabled } from '@posthog/react';
import { useState, useEffect } from 'react';
import { FormKey } from '../../../enums/formKey';
import { StepKey } from '../../../enums/stepKey';
import { StoreEvent } from '../../../enums/storeEvent';
import { QuoteResponse } from '../../../models/services/api/postQuote';
import { capturePostHogEvent } from '../../../services/posthog/capture';
import { useApplianceFormData } from '../../../store/selectors/useApplianceFormData';
import { useAppState } from '../../../store/selectors/useAppState';
import { useGaEventData } from '../../../store/selectors/useGaEventData';
import { useQuote } from '../../../store/selectors/useQuote';
import { useUrlParams } from '../../../store/selectors/useUrlParams';
import { store } from '../../../store/store';
import { getDiscountDisplayData } from '../../../utils/discounts/getDiscountDisplayData';
import { getSystemCode } from '../../../utils/getSystemCode';
import { formApplianceName } from '../../../utils/store/formApplianceName';
import { formCategoryName } from '../../../utils/store/formCategoryName';
import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { ArrowLeft } from '../../../assets/icons/ArrowLeft';
import { RadioButton } from '@domgen/base-design-system-2.0/atoms/RadioButton';
import { ShieldTickIcon } from '../../../assets/icons/ShieldTick';
import { MemoCircleCheck } from '../../../assets/icons/MemoCircleCheck';
import { applianceCareRadioOptionContent } from '../constants/applianceCareRadioOptionContent';
import { howApplianceCareWorksContent, ProtectionItem } from '../constants/howApplianceCareWorks';
import { getSpriteUrl } from '../../../assets/icons/spriteRegistry';
import { gaECommerceEvent, gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import { useNavigate } from 'react-router-dom';
import { controlRoutes } from '../../../routes/controlRoutes';
import { formatLocalisedDate } from '@domgen/shared/utils/date/formatLocalisedDate';
import { parseLocalDateString } from '@domgen/shared/utils/date/parseLocalDateString';

export type QuotePageTermTypes = 'appliance-care' | 'manufacturer-warranty';

const styles = {
  container: 'flex-1 flex flex-col items-center desktop:px-30 py-space-500',
  panel: 'max-w-300 p-space-500 gap-space-600 rounded-radius-100 bg-neutral-0',
  heading: 'text-text-headline b-space-200',
  subheading: 'text-body-large mb-space-500',
  coverOptionsSection: 'flex flex-col gap-space-400',
  applianceCareCard:
    'border rounded-radius-400 p-space-500 cursor-pointer transition-colors border-brand-primary bg-primary-50',
  applianceCareCardHeader:
    'flex items-start justify-between flex-row flex-wrap tablet:flex-row-reverse desktop:flex-row-reverse gap-space-300 pb-space-500 border-b  border-brand-primary',
  applianceCareCardTitleRow: 'flex items-start gap-space-300 ',
  applianceCareCardText: 'text-h3 text-text-headline',
  applianceCareCardSubtitle: 'text-body text-text-secondary-500 mb-space-500',
  badge: 'inline-flex items-center gap-space-200  text-body-small-strong px-space-400 py-space-200',
  badgeProtection: 'bg-success-600 text-neutral-0 text-h5',
  badgeRegOnly: 'border border-1  border-neutral-900 text-text-headline text-h5',
  benefitsList:
    'flex flex-col gap-space-400 list-none border-b border-brand-primary mb-space-500 pb-space-500 pt-space-500',
  benefitDescription: 'text-body-small',
  benefitTitle: 'text-body-large-strong',
  paymentNote: 'text-body-small text-text-secondary-500 mt-space-400',
  manufacturerCard:
    'border rounded-radius-400  border-brand-primary p-space-500 cursor-pointer transition-colors ',
  manufacturerCardContent: 'flex items-center gap-space-300 w-full',
  manufacturerCardHeader:
    'flex items-center justify-between flex-row flex-wrap tablet:flex-row-reverse desktop:flex-row-reverse gap-space-300',
  manufacturerCardSubtitle: 'text-body-small text-text-secondary-500 mt-space-200',
  errorText: 'text-body-small text-red-600 mt-space-400',
  actionRow:
    'flex flex-col w-full items-center justify-center md:flex-row-reverse md:justify-between pb-space-600',
  buttonWrapper: 'w-full md:w-fit mb-4 flex items-center justify-center',
  howApplianceCareWorksContainer:
    'flex flex-col items-start desktop:flex-row gap-space-600 px-space-500 py-space-400 border border-2 border-neutral-200 rounded-radius-400',
};

/**
 * This is  the variant_2_radio for Posthog in quote-page-jul-2026 experiment.
 */

export function QuotePageRadio() {
  const [selectedOption, setSelectedOption] = useState<QuotePageTermTypes | null>(null);
  const { VITE_CLIENT_FRIENDLY_NAME } = import.meta.env;
  const quote = useQuote();
  const { session } = useAppState();
  const formData = useApplianceFormData();
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);
  const posthog = usePostHog();
  const quoteData = quote?.data as QuoteResponse;
  const gaEventData = useGaEventData() || {};
  const applianceName = formApplianceName.get() ?? '';
  const applianceCategory = formCategoryName.get() ?? '';
  const brand = import.meta.env.VITE_CLIENT_FRIENDLY_NAME;
  const appSessionID = session?.id;
  const { experimentName, experimentVariant, netFirstPaymentAmount: offerPrice } = gaEventData;
  const navigate = useNavigate();

  const purchaseDate = formData?.[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE];
  const show2MonthsPromotion =
    useFeatureFlagEnabled(PosthogFeatureFlags.TWO_MONTHS_PROP) &&
    !!purchaseDate &&
    calculateAgeInMonths(purchaseDate) < 3;

  useEffect(() => {
    gaPageView({
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      appSessionID: session?.id,
      pagename: 'protection-details',
      offerPrice,
      experimentName,
      experimentVariant,
      quoteId: quoteData.offer.offerReference,
    });
  }, []);

  const discountTerms = getDiscountDisplayData(quoteData);

  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  const registerOnly = () => {
    store.send({ type: StoreEvent.REG_ONLY });
    capturePostHogEvent(posthog, 'protection-details-modal-register-only-cta-clicked', store.get());
  };

  const selectOption = (option: QuotePageTermTypes) => {
    setSelectedOption(option);

    //Prevent GA event firing when clicking the same radio button more than once
    if (selectedOption !== option) {
      gaEvent({
        event: 'genericGAEvent',
        eventCategory: 'AB test Quote ABCD',
        eventAction: 'radio button selected',
        eventLabel:
          option === 'appliance-care' ? 'Whirlpool Appliance Care' : 'Manufacturer warranty only',
        pagename: 'protection-details',
        category: 'registration-pages',
        type: 'STANDARD',
        regSystemCode: systemCode,
        applianceCategory,
        applianceName,
        appSessionID,
        offerPrice,
        experimentName,
        experimentVariant,
        quoteId: quoteData.offer.offerReference,
      });
    }
  };

  const payment = (
    <h3 className="text-headline">
      {!!discountTerms.netFirstPaymentAmountAfterPromotionalPeriod &&
        !show2MonthsPromotion &&
        discountTerms.netFirstPaymentAmount <
          discountTerms.netFirstPaymentAmountAfterPromotionalPeriod && (
          <span className="line-through text-button-text-outline-disabled mr-2">
            {currencyFormatter.format(discountTerms.netFirstPaymentAmountAfterPromotionalPeriod)}
          </span>
        )}
      {currencyFormatter.format(discountTerms.netFirstPaymentAmount)} a month
    </h3>
  );

  const handleContinue = () => {
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'protection_details',
      eventLabel: 'Continue with this selection',
      pagename: 'protection-details',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID,
      offerPrice,
      experimentName,
      experimentVariant,
      selected_flow:
        selectedOption === 'appliance-care'
          ? 'Whirlpool Appliance Care'
          : 'Manufacturer warranty only',
      quoteId: quoteData.offer.offerReference,
    });

    if (selectedOption === 'appliance-care') {
      gaECommerceEvent({
        event: 'add_to_cart',
        pagename: 'protection-details',
        regSystemCode: systemCode,
        version: 'WiB',
        currency: 'USD',
        appSessionID,
        experimentName,
        experimentVariant,
        quoteId: quoteData.offer.offerReference,
        ecommerce: {
          items: [
            {
              item_brand: brand,
              item_category: applianceCategory,
              item_appliance_type: applianceName,
              item_appliance_purchase_cost: Number(
                formData?.[StepKey.PURCHASE_PRICE]?.[FormKey.PURCHASE_PRICE],
              ),
              item_appliance_purchase_date: formatLocalisedDate(
                parseLocalDateString(
                  formData?.[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE] || '',
                ),
                'en-US',
              ),
              item_appliance_warranty: true,
              item_name: `${brand} ${applianceName}`,
              price: offerPrice,
              quantity: 1,
              quoteId: quoteData.offer.offerReference,
            },
          ],
        },
      });

      store.send({
        type: StoreEvent.CONTINUE_TO_CHECKOUT,
        data: { continueToCheckoutFromModal: false },
      });
    } else {
      registerOnly();
    }
  };

  const howApplianceCareWorks = howApplianceCareWorksContent(brand, getSpriteUrl(brand));

  const handleBack = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      pagename: 'protection-details',
      eventAction: 'GoBack CTA',
      eventLabel: 'success',
      category: 'registration-pages',
      type: 'STANDARD',
      applianceCategory,
      applianceName,
      appSessionID,
      offerPrice,
      experimentName,
      experimentVariant,
      quoteId: quoteData.offer.offerReference,
    });
    capturePostHogEvent(posthog, 'price-back-clicked', store.get());
    navigate(controlRoutes[controlRoutes.length - 1].path);
  };

  return (
    <div className={styles.container} data-testid="quote-page-radio">
      <div className={styles.panel}>
        <h1 className={styles.heading}>
          Never buy another {applianceName} again with {VITE_CLIENT_FRIENDLY_NAME} Appliance Care
        </h1>
        <p className={styles.subheading}>
          *Enhanced Protection you can rely on - if we can’t fix it, we’ll replace it with a brand
          new one.
        </p>

        <h2 className="text-h5 mb-space-200">Select one to continue</h2>
        <section className={styles.coverOptionsSection}>
          <article
            className={styles.applianceCareCard}
            onClick={() => selectOption('appliance-care')}
          >
            <div className={`${styles.applianceCareCardHeader}  `}>
              <span className={`${styles.badge} ${styles.badgeProtection}`}>
                <ShieldTickIcon />
                <p>Enhanced Protection</p>
              </span>

              <div>
                <div className={styles.applianceCareCardTitleRow}>
                  <RadioButton
                    checked={selectedOption === 'appliance-care'}
                    onChange={() => selectOption('appliance-care')}
                    name="cover-option"
                    value="replacement-care"
                    aria-label={`Select ${VITE_CLIENT_FRIENDLY_NAME} Replacement Care`}
                  />
                  <h3 className={styles.applianceCareCardText}>
                    {VITE_CLIENT_FRIENDLY_NAME} Appliance Care
                  </h3>
                </div>
              </div>
            </div>

            <ul className={styles.benefitsList}>
              {applianceCareRadioOptionContent.map((benefit, index) => (
                <li key={index}>
                  <div className="flex flex-row gap-space-400 items-start">
                    <div
                      className="text-accent-3-500 shrink-0 [&>svg]:w-6 [&>svg]:h-6"
                      style={{ width: '24px', height: '24px' }}
                    >
                      <benefit.icon />
                    </div>
                    <div className="flex flex-col gap-space-100">
                      <p className={styles.benefitTitle}>{benefit.title}</p>
                      <p className={styles.benefitDescription}>{benefit.description}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="col-span-4 flex flex-col gap-space-200 my-space-400">
              <h4 className="text-success-600 text-h4">
                Plus, get {discountTerms.discountPercentage}% off for {discountTerms.discountTerm}{' '}
                months{discountTerms.discountPaymentType === 'ach' ? '*' : ''}
              </h4>
              {payment}
              <p>Monthly plan, cancel anytime</p>
            </div>
            {discountTerms.discountPaymentType === 'ach' && (
              <p className="col-span-4 text-text-body">
                *Discount applies to payments made via bank account only. Applicable taxes
                calculated during checkout.
              </p>
            )}
          </article>

          <article
            className={styles.manufacturerCard}
            onClick={() => selectOption('manufacturer-warranty')}
          >
            <div className={`${styles.manufacturerCardHeader}`}>
              <span className={`${styles.badge} ${styles.badgeRegOnly}`}>
                <p className="text-headline text-text-body-strong">Registration Only</p>
              </span>

              <div className={styles.applianceCareCardTitleRow}>
                <RadioButton
                  checked={selectedOption === 'manufacturer-warranty'}
                  onChange={() => selectOption('manufacturer-warranty')}
                  name="cover-option"
                  value="manufacturer-warranty"
                  aria-label="Select Manufacturer Warranty"
                />
                <h3 className={styles.applianceCareCardText}>Manufacturer warranty only</h3>
              </div>
            </div>
          </article>
        </section>

        <article className="flex py-space-600 gap-space-400">
          <div className="shrink-0">
            <MemoCircleCheck />
          </div>
          <div className="flex flex-col items-start gap-space-400">
            <p>
              Limitations and exclusions apply. Please see the complete{' '}
              <a
                className="text-body-link-strong"
                href={import.meta.env.VITE_APPLIANCE_CARE_TERMS_AND_CONDITIONS_PATH}
                target="_blank"
                rel="noopener noreferrer"
              >
                {brand} Appliance Care terms and conditions
              </a>{' '}
              for details.
            </p>
          </div>
        </article>

        <section className={styles.actionRow}>
          <div className={styles.buttonWrapper}>
            <Button onClick={handleContinue} disabled={!selectedOption}>
              {selectedOption
                ? 'Continue with this selection'
                : 'Select protection level to continue'}
            </Button>
          </div>
          <TextLink onClick={handleBack} leftIcon={<ArrowLeft />}>
            Go back
          </TextLink>
        </section>

        <article className={styles.howApplianceCareWorksContainer}>
          <div>
            <h3 className="text-h3 mb-space-400">{howApplianceCareWorks.title}</h3>
            <div className="flex flex-col desktop:flex-row justify-start items-start gap-space-400">
              {howApplianceCareWorks.protection.map((content: ProtectionItem, index: number) => (
                <div key={index} className="flex flex-row items-center w-full desktop:max-w-95">
                  {content.icon}
                  <div className="ml-space-200">
                    <h2 className="text-body-large-strong">{content.heading}</h2>
                    <p>{content.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}
